/**
 * 头像 / 图片地址归一化。
 *
 * 背景：又拍云的自定义域名目前只提供 HTTP（https 握手直接失败），而站点本身已全站 HTTPS。
 * HTTPS 页面里放 http 图片会被浏览器当「混合内容」拦截：Chrome 会先把它自动升级成 https，
 * 升级失败就不再加载 —— 表现就是「头像有概率看不见」。
 *
 * 解决办法：HTTPS 页面下把所有 http 图片改写成后端同源的图片代理（GET /api/v1/files/image），
 * 由后端回源又拍云再以 HTTPS 吐出来。代理失败时由 UserAvatar 回退成昵称首字母，不会破图。
 */
import { API_BASE } from '@/api/config.js'

const IMAGE_PROXY = `${API_BASE}/api/v1/files/image`

/** 当前页面是否运行在 HTTPS 下（SSR / 异常环境一律按安全页面处理） */
function isSecurePage() {
  try {
    return window.location.protocol === 'https:'
  } catch (e) {
    return true
  }
}

function isHttp(value) {
  return /^http:\/\//i.test(value)
}

/**
 * 归一化图片地址（又拍云上的头像 / 背景图都适用）。
 *
 * @param {string} url 后端返回的图片地址（可能是完整 URL、相对路径或 null）
 * @returns {string} 可直接塞进 <img src> 的地址；为空时返回空串
 */
export function resolveImageUrl(url) {
  if (!url) return ''
  const raw = String(url).trim()
  if (!raw) return ''
  // 协议相对地址：//host/path —— 补上当前页面协议，避免被浏览器按 https 升级
  if (raw.indexOf('//') === 0) {
    return (isSecurePage() ? 'https:' : 'http:') + raw
  }
  // 相对路径：交给后端按配置的又拍云域名回源
  if (raw.charAt(0) === '/') {
    return `${IMAGE_PROXY}?url=${encodeURIComponent(raw)}`
  }
  // HTTPS 页面上的 HTTP 图片：改走后端同源 HTTPS 代理
  if (isHttp(raw) && isSecurePage()) {
    return `${IMAGE_PROXY}?url=${encodeURIComponent(raw)}`
  }
  return raw
}

/** 头像场景下的语义化别名，实现与 resolveImageUrl 完全一致 */
export const resolveAvatarUrl = resolveImageUrl

/**
 * 取昵称首字母用于头像兜底（兼容 emoji / 空昵称）。
 *
 * @param {string} name 昵称或备注名
 * @returns {string} 单个字符
 */
export function avatarFirstChar(name) {
  const text = String(name || '').trim()
  if (!text) return '?'
  return Array.from(text)[0] || '?'
}
